// src/hooks/useAuth.ts

import { developerWebLogin } from '@/api/auth';
import { getCurrentDeveloper } from '@/api/developer';
import type { DeveloperWebLoginRequest, DeveloperWebLoginResponse } from '@/types/auth';
import { useAuthStore } from '@/stores/authStore';
import { queryClient } from '@/lib/queryClient';

export const useAuth = () => {
  const { login } = useAuthStore();

  const loginDeveloper = async (
    credentials: DeveloperWebLoginRequest,
    navigate: (path: string, options?: { replace?: boolean }) => void
  ): Promise<DeveloperWebLoginResponse> => {
    try {
      // ✅ 处理 account 字段，将其转换为 email 或 phone
      const loginData: DeveloperWebLoginRequest = {
        password: credentials.password,
      };

      // ✅ 现在 TypeScript 知道 account 字段存在了
      if (credentials.account) {
        const account = credentials.account;
        // 邮箱正则（简单验证）
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (emailRegex.test(account)) {
          loginData.email = account;
        } else {
          loginData.phone = account;
        }
      }

      const response = await developerWebLogin(loginData);
      
      localStorage.setItem('developer_token', response.access_token);
      
      const developer = await getCurrentDeveloper();
      login(response.access_token, developer);
      
      navigate('/', { replace: true });
      
      return response;
    } catch (error) {
      if (error instanceof Error) {
        const message = error.message.toLowerCase();
        
        if (message.includes('401') || message.includes('unauthorized')) {
          throw new Error('认证失败：账号或密码错误');
        }
        if (message.includes('404')) {
          throw new Error('用户不存在，请先注册账号');
        }
        if (message.includes('network') || message.includes('timeout') || message.includes('fetch failed')) {
          throw new Error('网络连接失败，请检查网络');
        }
        if (message.includes('400')) {
          throw new Error('请求参数错误');
        }
      }
      
      throw error;
    }
  };

  const logout = (navigate: (path: string, options?: { replace?: boolean }) => void) => {
    const { logout: storeLogout } = useAuthStore.getState();
    storeLogout();
    
    queryClient.clear();
    
    navigate('/login', { replace: true });
  };

  return {
    loginDeveloper,
    logout,
  };
};